import {Backdrop, Box, CircularProgress, Typography} from "@mui/material";
import {grey} from "./style";
import {useEffect} from "react";

export const SHIPMENT_TYPE_GROUP_ID_PACKAGE = 5;
export const SHIPMENT_TYPE_GROUP_ID_LTL = 1;
export const SHIPMENT_TYPE_GROUP_ID_CROSS_BORDER = 9;
export const SHIPMENT_TYPE_GROUP_ID_MOVING_CAR = 6;
export const SHIPMENT_TYPE_GROUP_ID_MOVING_BOX = 7;
export const SHIPMENT_TYPE_GROUP_ID_MOVING_ITEM = 8;
export const SHIPMENT_TYPE_GROUP_ID_FLATBED = 3;
export const SHIPMENT_TYPE_GROUP_ID_FTL = 10;


export const convertMovingItemsToPackageData = (items) => {

    console.log('[convertMovingItemsToPackageData] items', items);

    // Constants
    const MAX_QUANTITY = 10;
    const MAX_DIMENSION = 108;
    const MAX_WEIGHT = 150;
    const MAX_LENGTH_PLUS_GIRTH = 165; // inches (length + 2×width + 2×height)

    // Initialize calculations
    let totalQuantity = 0;
    let totalWeight = 0;
    let maxWeight = 0;
    let maxLength = 0;
    let maxWidth = 0;
    let maxHeight = 0;
    const invalidItems = [];

    // Process each item
    items.forEach(item => {
        const quantity = parseInt(item.quantity);
        const length = parseInt(item.length);
        const width = parseInt(item.width);
        const height = parseInt(item.height);
        const weight = parseInt(item.weight);

        // Update totals
        totalQuantity += quantity;
        totalWeight += weight * quantity;

        // Track max dimensions
        maxLength = Math.max(maxLength, length);
        maxWidth = Math.max(maxWidth, width);
        maxHeight = Math.max(maxHeight, height);
        maxWeight = Math.max(maxWeight, weight);

        // Check for invalid items
        if (length > MAX_DIMENSION || width > MAX_DIMENSION ||
            height > MAX_DIMENSION || weight > MAX_WEIGHT) {
            invalidItems.push({
                name: item.name,
                length, width, height, weight
            });
        }
    });

    // Calculate average weight (weighted by quantity)
    const avgWeight = totalWeight / totalQuantity;

    console.log('[convertMovingItemsToPackageData] avgWeight', avgWeight);

    // Create a package object from moving items
    const packageObject = {
        length: maxLength,
        height: maxHeight,
        width: maxWidth,
        quantity: totalQuantity,
        weight: parseFloat(avgWeight.toFixed(2)),
        lengthUnit: 'in',
        weightUnit: 'lb'
    }

    console.log('[convertMovingItemsToPackageData] package object', packageObject);

    // Calculate package's length plus girth
    const lengthPlusGirth = maxLength + (2 * maxWidth) + (2 * maxHeight)

    console.log('[convertMovingItemsToPackageData] lengthPlusGirth', lengthPlusGirth);

    // Prepare result
    const result = {
        // Validation
        isValid: totalQuantity <= MAX_QUANTITY && invalidItems.length === 0 && lengthPlusGirth <= MAX_LENGTH_PLUS_GIRTH,
        totalQuantity,
        lengthPlusGirth,
        exceedsQuantity: totalQuantity > MAX_QUANTITY,
        exceedsLengthPlusGirth: lengthPlusGirth > MAX_LENGTH_PLUS_GIRTH,
        invalidItems,

        // Statistics
        dimensions: {
            maxLength,
            maxWidth,
            maxHeight
        },
        weights: {
            total: totalWeight,
            maxWeight,
            average: parseFloat(avgWeight.toFixed(2)) // Round to 2 decimals
        },
        packageData: packageObject
    };

    console.log('[convertMovingItemsToPackageData] result', result);

    return result;

}

export const hasChinese = (text) => {
    return /[\u4e00-\u9fff\u3400-\u4dbf\uf900-\ufaff\u3040-\u309f\u30a0-\u30ff\u3100-\u312f\u31a0-\u31bf\u3000-\u303f\uff00-\uffef]/.test(text);
}

export const generateCarShippingQuoteRequest = (data) => {
    const fromLocation = `${data.shipFromCity}, ${data.shipFromProvince}, ${data.shipFromCountry} (${data.shipFromPostalCode})`;
    const toLocation = `${data.shipToCity}, ${data.shipToProvince}, ${data.shipToCountry} (${data.shipToPostalCode})`;

    const carList = data.packageData
        .map(car => `${car.quantity}x ${car.yearOfProduction} ${car.make} ${car.model} (${car.categoryValue.replace("CAR-SIZE-", "").replace("-", " ")})`)
        .join(", ");

    return `Could you provide a detailed and conservative quote for transporting the following cars from ${fromLocation} to ${toLocation}: ${carList}? Please include estimated transit time and any relevant details.`;
}

export const getMissingKeysFromObject = (obj) => {
    console.log('[getMissingKeysFromObject] obj', obj);
    const missingKeys = Object.keys(obj).filter(key => {
        const value = obj[key];
        return (typeof value === "string" && value.trim() === "") || value === undefined || value === null;
    });

    return missingKeys;
}

export const getInvalidKeysFromObject = (obj) => {
    console.log('[getInvalidKeysFromObject] obj', obj);
    const invalidKeys = Object.keys(obj).filter(key => {
        const value = obj[key];
        return +value <= 0;
    });

    return invalidKeys;
}

export const setAPICallTimesInCookie = (key, value, expireMin) => {
    const date = new Date();
    date.setTime(date.getTime() + (expireMin * 60 * 1000));
    document.cookie = `${key}=${value}; expires=${date.toUTCString()}; path=/; ${
        process.env.NODE_ENV === 'production' ? 'domain=.uucargo.ca;' : ''
    }`;
};

export const getAPICallTimesFromCookie = (key) => {
    const cookie = document.cookie
        .split(';')
        .map(c => c.trim())
        .find(c => c.startsWith(`${key}=`));

    return cookie ? cookie.split('=')[1] : null;
};

export const checkAPICallLimit = (apiKey) => {
    const MAX_CALLS = 10;
    const TIME_WINDOW_MINUTES = 10;
    const cookieName = `apiLimit_${apiKey}`;

    // Get current call count & last call time
    const cookieData = getAPICallTimesFromCookie(cookieName);
    let callCount = 0;
    let lastCallTime = 0;

    if (cookieData) {
        const [storedCount, storedTime] = cookieData.split('|');
        callCount = parseInt(storedCount, 10);
        lastCallTime = parseInt(storedTime, 10);
    }

    // Check if time window has expired
    const currentTime = Date.now();
    const timeWindowMs = TIME_WINDOW_MINUTES * 60 * 1000;
    const timeElapsed = currentTime - lastCallTime;

    if (timeElapsed > timeWindowMs) {
        // Reset counter if time window expired
        callCount = 0;
    }

    // Check if limit exceeded
    if (callCount >= MAX_CALLS) {
        const nextAvailableTime = new Date(lastCallTime + timeWindowMs);
        return {
            allowed: false,
            remaining: 0,
            nextAvailable: nextAvailableTime
        };
    }

    // Update call count and timestamp
    const newCount = callCount + 1;
    setAPICallTimesInCookie(cookieName, `${newCount}|${currentTime}`, TIME_WINDOW_MINUTES);

    return {
        allowed: true,
        remaining: MAX_CALLS - newCount,
        nextAvailable: null
    };
};

export const extractFullAddress = place => {
    console.log(place)
    const address = {
        streetNumber: "",
        streetName: "",
        city: "",
        state: {},
        zip: "",
        country: {},
        neighborhood: "",
        sublocality: "",
        administrative_area_level_3: "",
        region: ""
    }

    if (!Array.isArray(place?.address_components)) {
        return address;
    }

    place.address_components.forEach(component => {
        const types = component.types;
        const value = component.long_name;
        const code = component.short_name;

        if (types.includes("street_number")) {
            address.streetNumber = value;
        }

        if (types.includes("route")) {
            address.streetName = value;
        }

        if (types.includes("locality")) {
            address.city = value;
        }

        if (types.includes("neighborhood")) {
            address.neighborhood = value;
        }

        if (types.includes("sublocality")) {
            address.sublocality = value;
        }

        if (types.includes("administrative_area_level_1")) {
            address.state = {
                name: value,
                code: getProvinceCodeOrName(code)
            }
        }

        if (types.includes("administrative_area_level_2")) {
            address.region = value;
        }

        if (types.includes("administrative_area_level_3")) {
            address.administrative_area_level_3 = value;
        }

        if (types.includes("postal_code")) {
            address.zip = value;
        }

        if (types.includes("country")) {
            address.country = {
                name: value,
                code
            }
        }
    });
    return address;
}

const provinceToCode = {
    "Alberta": "AB",
    "British Columbia": "BC",
    "Manitoba": "MB",
    "New Brunswick": "NB",
    "Newfoundland and Labrador": "NL",
    "Nova Scotia": "NS",
    "Ontario": "ON",
    "Prince Edward Island": "PE",
    "Quebec": "QC",
    "Saskatchewan": "SK",
    "Northwest Territories": "NT",
    "Nunavut": "NU",
    "Yukon": "YT",
    "Alabama": "AL",
    "Alaska": "AK",
    "Arizona": "AZ",
    "Arkansas": "AR",
    "California": "CA",
    "Colorado": "CO",
    "Connecticut": "CT",
    "Delaware": "DE",
    "Florida": "FL",
    "Georgia": "GA",
    "Hawaii": "HI",
    "Idaho": "ID",
    "Illinois": "IL",
    "Indiana": "IN",
    "Iowa": "IA",
    "Kansas": "KS",
    "Kentucky": "KY",
    "Louisiana": "LA",
    "Maine": "ME",
    "Maryland": "MD",
    "Massachusetts": "MA",
    "Michigan": "MI",
    "Minnesota": "MN",
    "Mississippi": "MS",
    "Missouri": "MO",
    "Montana": "MT",
    "Nebraska": "NE",
    "Nevada": "NV",
    "New Hampshire": "NH",
    "New Jersey": "NJ",
    "New Mexico": "NM",
    "New York": "NY",
    "North Carolina": "NC",
    "North Dakota": "ND",
    "Ohio": "OH",
    "Oklahoma": "OK",
    "Oregon": "OR",
    "Pennsylvania": "PA",
    "Rhode Island": "RI",
    "South Carolina": "SC",
    "South Dakota": "SD",
    "Tennessee": "TN",
    "Texas": "TX",
    "Utah": "UT",
    "Vermont": "VT",
    "Virginia": "VA",
    "Washington": "WA",
    "West Virginia": "WV",
    "Wisconsin": "WI",
    "Wyoming": "WY",
    "District of Columbia": "DC"
};

const getProvinceCodeOrName = (input) => {
    if (provinceToCode[input]) {
        return provinceToCode[input];
    } else {
        return input;
    }
};

export const getCountryCode = (accountBase = "ups", nafta = false) => {

    if (nafta) {
        return [
            {
                name: "Canada",
                code: "CA"
            },
            {
                name: "United States",
                code: "US"
            },
            {
                name: "Mexico",
                code: "MX"
            }
        ]
    }

    return [
        {
            name: "Argentina",
            code: "AR"
        },
        {
            name: "Australia",
            code: "AU"
        },
        {
            name: "Brazil",
            code: "BR"
        },
        {
            name: "Cambodia",
            code: "KH"
        },
        {
            name: "Canada",
            code: "CA"
        },
        {
            name: "China",
            code: "CN"
        },
        {
            name: "France",
            code: "FR"
        },
        {
            name: "Germany",
            code: "DE"
        },
        {
            name: "Hong Kong",
            code: "HK"
        },
        {
            name: "India",
            code: "IN"
        },
        {
            name: "Indonesia",
            code: "ID"
        },
        {
            name: "Italy",
            code: "IT"
        },
        {
            name: "Japan",
            code: "JP"
        },
        {
            name: "Laos People's Democratic LA Republic",
            code: "LA"
        },
        {
            name: "Malaysia",
            code: "MY"
        },
        {
            name: "Mexico",
            code: "MX"
        },
        {
            name: "Portugal",
            code: "PT"
        },
        {
            name: "Russia",
            code: "RU"
        },
        {
            name: "Saudi Arabia",
            code: "SA"
        },
        {
            name: "South Africa",
            code: "ZA"
        },
        {
            name: "South Korea",
            code: "KR"
        },
        {
            name: "Spain",
            code: "ES"
        },
        {
            name: "Taiwan",
            code: "TW"
        },
        {
            name: "Thailand",
            code: "TH"
        },
        {
            name: "Turkey",
            code: "TR"
        },
        {
            name: "United Kingdom",
            code: "GB"
        },
        {
            name: "United States",
            code: "US"
        },
        {
            name: "Vietnam",
            code: "VN"
        },
    ]
}

export const carManufactures = [
    "Abarth",
    "Alfa Romeo",
    "Aston Martin",
    "Audi",
    "Bentley",
    "BMW",
    "Bugatti",
    "Cadillac",
    "Chevrolet",
    "Chrysler",
    "Citroën",
    "Dacia",
    "Daewoo",
    "Daihatsu",
    "Dodge",
    "Donkervoort",
    "DS",
    "Ferrari",
    "Fiat",
    "Fisker",
    "Ford",
    "Honda",
    "Hummer",
    "Hyundai",
    "Infiniti",
    "Iveco",
    "Jaguar",
    "Jeep",
    "Kia",
    "KTM",
    "Lada",
    "Lamborghini",
    "Lancia",
    "Land Rover",
    "Landwind",
    "Lexus",
    "Lincoln",
    "Lotus",
    "Maserati",
    "Maybach",
    "Mazda",
    "McLaren",
    "Mercedes-Benz",
    "MG",
    "Mini",
    "Mitsubishi",
    "Morgan",
    "Nissan",
    "Opel",
    "Peugeot",
    "Porsche",
    "Renault",
    "Rolls-Royce",
    "Rover",
    "Saab",
    "Seat",
    "Skoda",
    "Smart",
    "SsangYong",
    "Subaru",
    "Suzuki",
    "Tesla",
    "Toyota",
    "Volkswagen",
    "Volvo"
]

export const numArray = (start, end, order = 'asc') => {
    const array = Array.from({length: end - start + 1}, (_, i) => start + i);
    if (order === 'desc') {
        array.reverse();
    }
    return array.map(year => year.toString());
}

export const checkHSCodeInValid = (errorList, hsCode) => {
    console.log('[checkHSCodeInValid] errorList', errorList);
    console.log('[checkHSCodeInValid] hsCode', hsCode);

    return errorList?.some(item => +item?.hsCode === +hsCode)
}

export const LoadingComponent = ({loading, message}) => {
    return (
        <Backdrop
            sx={(theme) => ({zIndex: theme.zIndex.drawer + 1})}
            open={loading}
        >
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <Typography sx={{color: grey}}>
                    {message}
                </Typography>
                <CircularProgress size={50}/>
            </Box>
        </Backdrop>
    )
}

// validate phone
export const isValidatePhone = (value) => {
    const cleanedString = value?.replace(/[^a-zA-Z0-9]/g, '');
    const phoneNumberRegex = /^\d{10,15}$/;
    return phoneNumberRegex.test(cleanedString);
}

export const PARCEL_PACKAGE_PROMPT = 'Thank you for choosing Parcel Package Service. To proceed, I will need the shipper and consignee postal code and country, as well as the package details. Could you please provide this information?';
export const LTL_FREIGHT_PROMPT = 'Thank you for choosing LTL Freight Service. To proceed, I will need the shipper and consignee postal code and country, as well as the pallet details. Could you please provide this information?';
export const MOVING_SERVICE_PROMPT = 'Thank you for choosing Local & Long Distance Moving Service. To proceed, I will need the shipper and consignee postal code and country, as well as the item details or house type/size. Could you please provide this information?';
export const CAR_SHIPPING_PROMPT = 'Thank you for choosing Car Shipping Service. To proceed, I will need the shipper and consignee postal code and country, as well as the vehicle details. Could you please provide this information?';
export const CUSTOMS_BROKERAGE_PROMPT = 'Thank you for choosing Customs Brokerage Service. To proceed, I will need the export country, import country, as well as the item details. If the import country is Canada, the United States, or Mexico, please also include the destination province or state. Could you please provide this information?';
export const HS_CODE_LOOKUP_PROMPT = 'Thank you for choosing our HS Code Lookup Service. To proceed, I will need the export country, import country, original country of item, and item description. Could you please provide this information?'

export const PARCEL_PACKAGE_PROMPT_CN = '感谢您选择包裹快递服务。为了继续办理，我需要获取寄件人和收件人的邮政编码与国家信息，以及包裹详细信息。请提供这些资料好吗？';
export const LTL_FREIGHT_PROMPT_CN = '感谢您选择LTL零担货运服务。为了继续办理，我需要获取寄件人和收件人的邮政编码与国家信息，以及托盘详细信息。请提供这些资料好吗？';
export const MOVING_SERVICE_PROMPT_CN = '感谢您选择同城及长途搬家服务。为了继续办理，我需要获取寄件人和收件人的邮政编码与国家信息，以及物品详细信或息房屋的類型/大小。请提供这些资料好吗？';
export const CAR_SHIPPING_PROMPT_CN = '感谢您选择车辆运输服务。为了继续办理，我需要获取寄件人和收件人的邮政编码与国家信息，以及车辆详细信息。请提供这些资料好吗？';
export const CUSTOMS_BROKERAGE_PROMPT_CN = '感谢您选择报关代理服务。为了继续办理，我需要获取出口国、进口国以及物品详细信息。若进口国为加拿大、美国或墨西哥，请同时提供目的省份/州信息。请提供这些资料好吗？';
export const HS_CODE_LOOKUP_PROMPT_CN = '感谢您选择海关编码查询服务。为了继续办理，我需要获取出口国、进口国、物品原产国及商品描述信息。请提供这些资料好吗？';

export const PARCEL_PACKAGE = {
    code: "PARCEL_PACKAGE",
    name: "Parcel Package Service",
    nameCN: '包裹快递服务',
    prompt: PARCEL_PACKAGE_PROMPT,
    promptCN: PARCEL_PACKAGE_PROMPT_CN,
    listName: 'Package',
    listNameCN: '包裹',
    shipmentTypeGroupId: SHIPMENT_TYPE_GROUP_ID_PACKAGE
};

export const LTL_FREIGHT = {
    code: "LTL_FREIGHT",
    name: "LTL Freight Service",
    nameCN: 'LTL零担货运服务',
    prompt: LTL_FREIGHT_PROMPT,
    promptCN: LTL_FREIGHT_PROMPT_CN,
    listName: 'Pallet',
    listNameCN: '托盘',
    shipmentTypeGroupId: SHIPMENT_TYPE_GROUP_ID_LTL
};

export const MOVING_SERVICE = {
    code: "MOVING_SERVICE",
    name: "Local & Long Distance Moving Service",
    nameCN: '同城及长途搬家服务',
    prompt: MOVING_SERVICE_PROMPT,
    promptCN: MOVING_SERVICE_PROMPT_CN,
    listName: 'Item',
    listNameCN: '物品',
    shipmentTypeGroupId: SHIPMENT_TYPE_GROUP_ID_MOVING_ITEM
};

export const CAR_SHIPPING = {
    code: "CAR_SHIPPING",
    name: "Car Shipping Service",
    nameCN: '车辆运输服务',
    prompt: CAR_SHIPPING_PROMPT,
    promptCN: CAR_SHIPPING_PROMPT_CN,
    listName: 'Vehicle',
    listNameCN: '车辆',
    shipmentTypeGroupId: SHIPMENT_TYPE_GROUP_ID_MOVING_CAR
};

export const CUSTOMS_BROKERAGE = {
    code: "CUSTOMS_BROKERAGE",
    name: "Customer Brokerage Service",
    nameCN: '报关代理服务',
    prompt: CUSTOMS_BROKERAGE_PROMPT,
    promptCN: CUSTOMS_BROKERAGE_PROMPT_CN,
    shipmentTypeGroupId: SHIPMENT_TYPE_GROUP_ID_CROSS_BORDER
};

export const HS_CODE_LOOKUP = {
    code: "HS_CODE_LOOKUP",
    name: "HS Code Lookup Service",
    nameCN: '海关编码查询服务',
    prompt: HS_CODE_LOOKUP_PROMPT,
    promptCN: HS_CODE_LOOKUP_PROMPT_CN,
};

export const getServiceFromServiceCode = (serviceCode) => {
    switch (serviceCode) {
        case PARCEL_PACKAGE?.code:
            return PARCEL_PACKAGE;
        case LTL_FREIGHT?.code:
            return LTL_FREIGHT;
        case MOVING_SERVICE?.code:
            return MOVING_SERVICE;
        case CAR_SHIPPING?.code:
            return CAR_SHIPPING;
        case CUSTOMS_BROKERAGE?.code:
            return CUSTOMS_BROKERAGE;
        case HS_CODE_LOOKUP?.code:
            return HS_CODE_LOOKUP;
        default:
            return ""
    }
}

export const convertDataFromServiceCode = (serviceCode, data) => {

    console.log('[convertDataFromServiceCode] serviceCode', serviceCode);

    console.log('[convertDataFromServiceCode] data', data);


    switch (serviceCode) {
        case PARCEL_PACKAGE?.code:
            return {
                shipper_address: data?.shipper_address || data?.data?.shipper_address,
                consignee_address: data?.consignee_address || data?.data?.consignee_address,
                package: data?.package || data?.data?.package
            };
        case LTL_FREIGHT?.code:
            return {
                shipper_address: data?.shipper_address || data?.data?.shipper_address,
                consignee_address: data?.consignee_address || data?.data?.consignee_address,
                pallet: data?.pallet || data?.data?.pallet
            };
        case MOVING_SERVICE?.code:
            return {
                shipper_address: data?.shipper_address || data?.data?.shipper_address,
                consignee_address: data?.consignee_address || data?.data?.consignee_address,
                item: data?.item || data?.data?.item,
                pickup: data?.pickup || data?.data?.pickup,
                delivery: data?.delivery || data?.data?.delivery,
                expected_moving_date: data?.expected_moving_date || data?.data?.expected_moving_date
            };
        case CAR_SHIPPING?.code:
            return {
                shipper_address: data?.shipper_address || data?.data?.shipper_address,
                consignee_address: data?.consignee_address || data?.data?.consignee_address,
                vehicle: data?.vehicle || data?.data?.vehicle
            };
        // case CUSTOMS_BROKERAGE?.code:
        //     return CUSTOMS_BROKERAGE;
        // case HS_CODE_LOOKUP?.code:
        //     return HS_CODE_LOOKUP;
        default:
            return ""
    }
}

export const formatTariffCode = (input) => {
    if (!input) return '';

    // Remove any non-digit characters
    const digitsOnly = input.toString().replace(/\D/g, '');

    // Just add dots at fixed positions regardless of length
    if (digitsOnly.length <= 4) {
        return digitsOnly;
    } else if (digitsOnly.length <= 6) {
        return `${digitsOnly.slice(0, 4)}.${digitsOnly.slice(4)}`;
    } else if (digitsOnly.length <= 8) {
        return `${digitsOnly.slice(0, 4)}.${digitsOnly.slice(4, 6)}.${digitsOnly.slice(6)}`;
    } else {
        return `${digitsOnly.slice(0, 4)}.${digitsOnly.slice(4, 6)}.${digitsOnly.slice(6, 8)}.${digitsOnly.slice(8)}`;
    }
};

export const isValidTariffCode = (input) => {

    // Remove all formatting and check digit requirements
    const digitsOnly = input?.toString()?.replace(/\D/g, '');

    // Must have at least 8 digits
    if (digitsOnly?.length < 8 || digitsOnly?.length > 10) {
        return false;
    }

    const pattern = /^\d{4}(\.\d{2}(\.\d{2}(\.\d{2})?)?)?$/;

    // First check if already valid
    if (pattern.test(input)) {
        return true;
    }

    // If not valid, format it and check again
    const formatted = formatTariffCode(input);
    return pattern.test(formatted);
}


export const useIframeResizer = () => {
    useEffect(() => {
        // 如果不在 iframe 中则不执行
        if (window.self === window.top) return;

        const resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                // 获取当前组件占用的实际高度
                const height = entry.target.scrollHeight;
                window.parent.postMessage({
                    type: 'setIframeHeight',
                    height: height
                }, '*');
            }
        });

        // 监听 body 的大小变化（因为 Container 宽度 100%）
        resizeObserver.observe(document.body);

        return () => resizeObserver.disconnect();
    }, []);
};

export const LTLLogo = [
    {
        url: "https://storage.googleapis.com/uucargo-resource/image/icon/J-Link.png",
        serviceName: "j-link",
        officialName: "J-Link"
    },
    {
        url: "https://storage.googleapis.com/uucargo-resource/image/icon/TForce.png",
        serviceName: "tforce",
        officialName: "TForce"
    },
    {
        url: "https://storage.googleapis.com/uucargo-resource/image/icon/tfww.png",
        serviceName: "tforce-worldwide",
        officialName: "TForce Worldwide"
    },
    {
        url: "https://storage.googleapis.com/uucargo-resource/image/icon/meadallionIcon.jpg",
        serviceName: "medallion",
        officialName: "Medallion"
    },
    {
        url: "https://storage.googleapis.com/uucargo-resource/image/icon/UUCargoLogo.png",
        serviceName: "uucargo",
        officialName: "UUCargo"
    }
]

export const mapLTLLogo = (serviceName) => {
    const service = LTLLogo.filter(item => item?.serviceName?.toUpperCase() === serviceName?.toUpperCase());
    // console.log(service);
    return service[0]?.url
}

